import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import {
  BRAND_IMAGE_TAG_META,
  MAX_BRAND_IMAGES,
  VALID_BRAND_IMAGE_TAGS,
} from '@mondaygpt/constants'
import type { BrandImageTag } from '@mondaygpt/constants'
import { useAnalytics } from 'hooks'
import { Loader2, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { BrandImageTagModal } from './BrandImageTagModal'
import React, { useCallback, useRef, useState } from 'react'
import { patchBrandKit } from '../api/brand-kit-api'
import { UploadError, useUploadFile } from '../hooks/useUploadFile'
import { useBrandKitStore } from '../store/brand-kit-store'
import type { BrandImage } from '../types'

interface BrandImageLibrarySectionProps {
  /**
   * 'categories' (default) renders one labelled row per tag (Brand Kit page).
   * 'flat' renders a single row of all images + one add tile (onboarding
   * panel) — uploads land as 'products'; tapping a tile opens the tag modal.
   */
  variant?: 'categories' | 'flat'
}

export const BrandImageLibrarySection: React.FC<BrandImageLibrarySectionProps> = ({
  variant = 'categories',
}) => {
  const { track } = useAnalytics()
  const images = useBrandKitStore(s => s.brandKit.images)
  const updateBrandKit = useBrandKitStore(s => s.updateBrandKit)
  const uploadFile = useUploadFile()
  const [uploadingTag, setUploadingTag] = useState<string | null>(null)
  const [tagModalImage, setTagModalImage] = useState<BrandImage | null>(null)

  const untaggedImages = images.filter(img => img.tags.length === 0 && img.attachmentId)

  // Use ref to avoid stale closures in async callbacks
  const imagesRef = useRef(images)
  imagesRef.current = images

  const saveImages = useCallback(
    async (newImages: BrandImage[]) => {
      updateBrandKit({ images: newImages })
      await patchBrandKit({
        images: newImages.map(img => ({ attachmentId: img.attachmentId, tags: img.tags })),
      })
    },
    [updateBrandKit]
  )

  const handleFileSelect = useCallback(
    async (tag: BrandImageTag, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      e.target.value = ''

      if (imagesRef.current.length >= MAX_BRAND_IMAGES) {
        // Never fail silently at the cap: a trial user reported "uploads broken"
        // on four separate days because the "+" tile just vanished.
        toast.error(
          `Your image library is full (${MAX_BRAND_IMAGES} images). Remove one to add another.`
        )
        return
      }

      const blobUrl = URL.createObjectURL(file)
      const tempImage: BrandImage = { attachmentId: '', tags: [tag], publicUrl: blobUrl }

      updateBrandKit({ images: [...imagesRef.current, tempImage] })
      setUploadingTag(tag)

      try {
        const { attachmentId, publicUrl } = await uploadFile(file, {
          maxBytes: 10 * 1024 * 1024,
        })
        URL.revokeObjectURL(blobUrl)
        const uploaded: BrandImage = {
          attachmentId,
          tags: [tag],
          publicUrl,
        }
        const final = imagesRef.current.filter(img => img.publicUrl !== blobUrl).concat(uploaded)
        await saveImages(final)
        track(AnalyticsEvents.BrandKit.BRAND_IMAGE_UPLOADED, {
          tag,
          total_images: final.length,
        })
      } catch (err) {
        URL.revokeObjectURL(blobUrl)
        updateBrandKit({ images: imagesRef.current.filter(img => img.publicUrl !== blobUrl) })
        if (err instanceof UploadError && err.reason === 'too_large') {
          toast.error(`${file.name} is too large (max 10MB)`)
        } else {
          toast.error('Failed to upload image')
        }
      } finally {
        setUploadingTag(null)
      }
    },
    [uploadFile, saveImages, updateBrandKit, track]
  )

  const handleRemove = useCallback(
    async (attachmentId: string) => {
      const newImages = imagesRef.current.filter(img => img.attachmentId !== attachmentId)
      await saveImages(newImages)
      track(AnalyticsEvents.BrandKit.BRAND_IMAGE_REMOVED, { total_images: newImages.length })
    },
    [saveImages, track]
  )

  const handleTagsSaved = useCallback(
    async (attachmentId: string, tags: BrandImageTag[]) => {
      const newImages = imagesRef.current.map(img =>
        img.attachmentId === attachmentId ? { ...img, tags } : img
      )
      await saveImages(newImages)
      track(AnalyticsEvents.BrandKit.BRAND_IMAGE_TAGS_UPDATED, { tags })
      setTagModalImage(null)
    },
    [saveImages, track]
  )

  const renderTile = (img: BrandImage) => {
    const uploading = !img.attachmentId
    return (
      <div
        key={img.attachmentId || img.publicUrl}
        className="group relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-[12px] border border-line bg-bg-3"
      >
        <img
          src={img.publicUrl || ''}
          alt=""
          className={`h-full w-full object-cover ${uploading ? 'opacity-40' : ''}`}
        />
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 size={16} className="animate-spin text-acid" />
          </div>
        )}
        {!uploading && (
          <>
            <button
              type="button"
              onClick={() => setTagModalImage(img)}
              className="absolute inset-0 cursor-pointer"
            />
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                handleRemove(img.attachmentId)
              }}
              className="absolute top-1 right-1 z-10 rounded-full border border-line-bright bg-bg-4 p-0.5 text-paper-dim opacity-0 transition-opacity group-hover:opacity-100 hover:text-paper"
            >
              <X size={10} />
            </button>
          </>
        )}
      </div>
    )
  }

  const atCap = images.length >= MAX_BRAND_IMAGES

  // A visible counter + an explicit at-cap tile. The old behavior silently
  // hid the "+" tile at 16 images, which read as "uploads are broken".
  const counter = (
    <span className={`text-[11px] ${atCap ? 'font-medium text-paper-dim' : 'text-paper-faint'}`}>
      {images.length}/{MAX_BRAND_IMAGES} images
      {atCap ? ' · library full, remove one to add more' : ''}
    </span>
  )

  const renderAddTile = (tag: BrandImageTag) =>
    atCap ? (
      <div
        className="flex h-20 w-20 flex-shrink-0 flex-col items-center justify-center gap-0.5 rounded-[12px] border-2 border-dashed border-line text-paper-faint"
        title={`Your image library is full (${MAX_BRAND_IMAGES} images). Remove one to add another.`}
      >
        <span className="text-[11px] font-medium">
          {images.length}/{MAX_BRAND_IMAGES}
        </span>
        <span className="text-[10px]">Full</span>
      </div>
    ) : (
      <label className="flex h-20 w-20 flex-shrink-0 cursor-pointer items-center justify-center rounded-[12px] border-2 border-dashed border-line text-paper-faint transition-colors hover:border-acid hover:bg-[color:var(--acid-tint)] hover:text-acid">
        {uploadingTag === tag ? <Loader2 size={16} className="animate-spin" /> : <Plus size={18} />}
        <input
          type="file"
          accept="image/*"
          className="pointer-events-none fixed top-0 left-0 h-px w-px opacity-0"
          onChange={e => handleFileSelect(tag, e)}
        />
      </label>
    )

  const tagModal = tagModalImage && (
    <BrandImageTagModal
      image={tagModalImage}
      onClose={() => setTagModalImage(null)}
      onSave={tags => handleTagsSaved(tagModalImage.attachmentId, tags)}
    />
  )

  if (variant === 'flat') {
    return (
      <div>
        <div className="mb-1.5 flex justify-end">{counter}</div>
        <div className="flex gap-2.5 overflow-x-auto pb-1">
          {images.map(renderTile)}
          {renderAddTile('products')}
        </div>
        {tagModal}
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="-mb-3 flex justify-end">{counter}</div>
      {VALID_BRAND_IMAGE_TAGS.map(tag => {
        const meta = BRAND_IMAGE_TAG_META[tag]
        const categoryImages = images.filter(img => img.tags.includes(tag))

        return (
          <div key={tag}>
            <div className="mb-2">
              <span className="text-[12px] font-medium text-paper">{meta.label}</span>
              <span className="ml-1.5 text-[12px] text-paper-faint">{meta.description}</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {categoryImages.map(renderTile)}
              {renderAddTile(tag)}
            </div>
          </div>
        )
      })}

      {/* Untagged images section */}
      {untaggedImages.length > 0 && (
        <div>
          <div className="mb-2">
            <span className="text-[12px] font-medium text-paper">Untagged</span>
            <span className="ml-1.5 text-[12px] text-paper-faint">Tap to add tags</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">{untaggedImages.map(renderTile)}</div>
        </div>
      )}

      {tagModal}
    </div>
  )
}
